import { getExample, parsePhoneNumber } from 'awesome-phonenumber';
import dayjs from 'dayjs';
import { t } from 'i18next';
import * as yup from 'yup';

import { store } from './configureStore';

const EMAIL_REGEX =
	// eslint-disable-next-line no-useless-escape, sonarjs/concise-regex
	/^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;

const IBAN_REGEX =
	/^(?:(?:CR|DE|ME|RS|VA)\d{20}|(?:CZ|ES|SE|SK|TN)\d{22}|(?:DK|FI|FO|GL|SD)\d{16}|(?:AT|BA|EE|LT|XK)\d{18}|(?:AE|IL|TL)\d{21}|(?:LY|PT|ST)\d{23}|(?:IT|SM)\d{2}[A-Z]\d{10}[A-Za-z0-9]{12}|(?:HU|PL)\d{26}|(?:AL|CY)\d{10}[A-Za-z0-9]{16}|(?:CH|LI)\d{7}[A-Za-z0-9]{12}|(?:FR|MC)\d{12}[A-Za-z0-9]{11}\d{2}|(?:GB|IE)\d{2}[A-Z]{4}\d{14}|(?:KZ|LU)\d{5}[A-Za-z0-9]{13}|(?:GI|IQ)\d{2}[A-Z]{4}[A-Za-z0-9]{15}|(?:PK|RO)\d{2}[A-Z]{4}[A-Za-z0-9]{16}|(?:PS|QA)\d{2}[A-Z]{4}[A-Za-z0-9]{21}|AD\d{10}[A-Za-z0-9]{12}|AZ\d{2}[A-Z]{4}[A-Za-z0-9]{20}|BE\d{14}|BG\d{2}[A-Z]{4}\d{6}[A-Za-z0-9]{8}|BH\d{2}[A-Z]{4}[A-Za-z0-9]{14}|BR\d{25}[A-Z][A-Za-z0-9]|BY\d{2}[A-Za-z0-9]{4}\d{4}[A-Za-z0-9]{16}|DO\d{2}[A-Za-z0-9]{4}\d{20}|EG\d{27}|GE\d{2}[A-Z]\d{16}|GT\d{2}[A-Za-z0-9]{24}|GR\d{9}[A-Za-z0-9]{16}|HR\d{19}|IS\d{24}|JO\d{2}[A-Z]{4}\d{4}[A-Za-z0-9]{18}|KW\d{2}[A-Z]{4}[A-Za-z0-9]{22}|LC\d{2}[A-Z]{4}[A-Za-z0-9]{24}|LB\d{6}[A-Za-z0-9]{20}|LV\d{2}[A-Z]{4}\d{13}|MD\d{2}[A-Za-z0-9]{20}|MK\d{5}[A-Za-z0-9]{10}\d{2}|MR\d{25}|MT\d{2}[A-Z]{4}\d{5}[A-Za-z0-9]{18}|MU\d{2}[A-Z]{4}\d{19}[A-Z]{3}|NL\d{2}[A-Z]{4}\d{10}|NO\d{13}|SA\d{4}[A-Za-z0-9]{18}|SC\d{2}[A-Z]{4}\d{20}[A-Z]{3}|SI\d{17}|SV\d{2}[A-Z]{4}\d{20}|TR\d{8}[A-Za-z0-9]{16}|UA\d{8}[A-Za-z0-9]{19}|VG\d{2}[A-Z]{4}\d{16}|GE\d{2}[A-Z]{2}\d{16})$/;

yup.setLocale({
	mixed: {
		required: t('alert.requiredField'),
		notType: t('alert.requiredField')
	},
	array: {
		required: t('alert.requiredField')
	},
	string: {
		email: t('validation.invalidEmailFormat'),
		min: (props) =>
			t('validation.stringFieldMinLength', {
				fieldName: t(props.path.split('].').pop()),
				length: props.min
			}),
		max: (props) =>
			t('validation.stringFieldMaxLength', {
				fieldName: t(props.path.split('].').pop()),
				length: props.max
			}),
		url: t('validation.invalidUrlFormat')
	},
	number: {
		min: (props) =>
			t('validation.numberFieldMinLength', {
				fieldName: t(props.path.split('].').pop()),
				length: props.min
			}),
		max: (props) =>
			t('validation.numberFieldMaxLength', {
				fieldName: t(props.path.split('].').pop()),
				length: props.max
			}),
		positive: () => t('validation.numberFieldPositive')
	}
});

yup.addMethod(yup.string, 'email', function validateEmail() {
	return this.matches(EMAIL_REGEX, {
		message: t('validation.invalidEmailFormat'),
		name: 'email',
		excludeEmptyString: true
	});
});

yup.addMethod(yup.string, 'json', function (errorMessage) {
	return this.test('isJSON', errorMessage, function (value) {
		const { path, createError } = this;

		if (!value) return true;
		try {
			JSON.parse(value);
			return true;
		} catch {
			return createError({ path, message: errorMessage ?? 'validation.invalidJSON' });
		}
	});
});

yup.addMethod(yup.string, 'phoneNumber', function () {
	return this.test('testPhoneNumber', null, function (value) {
		const { path, createError, schema } = this;
		const { nullable, optional } = schema.spec;
		if (value === null && nullable && optional) return true;

		let isValid = false;
		try {
			isValid = parsePhoneNumber(`+${value}`).valid;
		} catch {
			// do nothing
		}

		if (isValid) {
			return true;
		}

		let example = '';

		try {
			example = getExample(parsePhoneNumber(`+${value}`).regionCode, 'mobile')?.number?.international;
		} catch {
			// do nothing
		}

		return createError({
			path,
			message: (
				<div
					style={{
						display: 'flex',
						flexDirection: 'row',
						gap: '0.5rem'
					}}>
					{t('validation.invalidPhoneWithExample')}
					{example && (
						<div
							style={{
								direction: 'ltr'
							}}>
							{example}
						</div>
					)}
				</div>
			)
		});
	});
});

yup.addMethod(yup.object, 'noOverlappingHours', function () {
	return this.test('no-overlap', t('validation.noOverlap'), function (value) {
		const { path, createError } = this;
		if (!value?.shift?.length || value.shift.length <= 1) return true;

		const isOverlapping = (end1, start2) => {
			if (end1 <= start2) return false;
			if (end1 > 24 || start2 > 24) return end1 % 24 > start2 % 24;
			return true;
		};

		// Sort shifts by start time
		const sortedShifts = [...value.shift].sort((a, b) => a.start - b.start);

		for (let i = 1; i < sortedShifts.length; i += 1) {
			const prevShift = sortedShifts[i - 1];
			const currentShift = sortedShifts[i];

			if (isOverlapping(prevShift.end, currentShift.start)) {
				return createError({
					path: `${path}.shift[${i}].start`,
					message: 'validation.noOverlap'
				});
			}
		}

		return true;
	});
});

yup.addMethod(yup.object, 'validShiftTimes', function () {
	return this.test('valid-shift-times', t('validation.invalidShiftTimes'), function (shift) {
		const { path, createError } = this;

		const start = dayjs(new Date(`2000-01-01T${shift.start}`));
		const end = dayjs(new Date(`2000-${shift.end === '00:00' ? '02' : '01'}-01T${shift.end}`));

		if (end.isBefore(start) || end.isSame(start)) {
			return createError({
				path: `${path}.end`,
				message: t('validation.endTimeMustBeAfterStartTime')
			});
		}

		return true;
	});
});

yup.addMethod(yup.string, 'isCountryAllowed', function () {
	return this.test('isCountryAllowed', t('error.notInAllowedCountries'), (countryName) => {
		const { planName = null, localization = {} } = store.getState().user;
		const country = localization.countryISO2 || '';
		const allowedCountries = localization.allowedCountries
			? [...localization.allowedCountries.split(','), country].filter(Boolean)
			: [country];

		if (planName === 'EnterprisePackage') return true;
		if (['ScalePackage', 'StarterPackage'].includes(planName) && allowedCountries.includes(countryName)) {
			return true;
		}
		return planName === 'FreePackage' && countryName === country;
	});
});

yup.addMethod(yup.string, 'hsCode', function () {
	return this.test('isInternationalOrder', null, function (value, context) {
		const { path, createError } = this;
		if (context?.from[context.from.length - 1]?.value?.isInternationalOrder) {
			if (!value) {
				return createError({
					path,
					message: t('alert.requiredField')
				});
			} else {
				return true;
			}
		} else {
			return true;
		}
	});
});

yup.addMethod(yup.string, 'hsCodeLenght', function () {
	return this.test('hsCodeLength', null, function (value, context) {
		const { path, createError } = this;
		const isInternationalOrder = context?.from[context.from.length - 1]?.value?.isInternationalOrder;
		const countryName = context?.from[context.from.length - 1]?.value?.address?.countryName;

		if (isInternationalOrder && countryName === 'KW') {
			if (!value || value.length !== 12) {
				return createError({
					path,
					message: t('alert.hsCodeRequirementsForKuwait')
				});
			}
		}

		return true;
	});
});

yup.addMethod(yup.string, 'requiredZipCode', function () {
	return this.test('requiredZipCode', t('alert.requiredField'), function (value) {
		const { path, createError } = this;
		const countryName = this.resolve(yup.ref('countryName'));
		const { countryList } = store.getState().globalAddress;

		const selectedCountry = countryList?.find((country) => country.shortCode === countryName);

		if (selectedCountry && selectedCountry.zipCodeRequired === 'yes') {
			if (!value) {
				return createError({ path, message: t('alert.requiredField') });
			}
		}

		return true;
	});
});

yup.addMethod(yup.string, 'customOrderCurrencyValidation', function () {
	return this.when(['products', 'address', 'originCountry'], ([products, address, originCountry], schema) => {
		let updatedSchema = schema;
		if (products?.length > 0) {
			updatedSchema = updatedSchema.test({
				test: (value) => products.every((product) => (product?.currency ? product.currency === value : true)),
				message: t('errorMsg.orderAndProductsCurrencyMismatch')
			});
		}
		if (address?.countryName && originCountry) {
			updatedSchema = updatedSchema.test({
				test: (value) => {
					if (
						originCountry === 'TR' &&
						['QA', 'SA', 'KSA', 'OM', 'JO', 'KW', 'LB', 'BH', 'EG', 'AE'].includes(address?.countryName)
					) {
						return value === 'USD' || value === 'EUR';
					} else {
						return true;
					}
				},
				message: t('errorMsg.currencyShouldBeUSDorEUR')
			});
		}
		return updatedSchema;
	});
});

yup.addMethod(yup.string, 'polygon', function (message) {
	return this.matches('^[+-]?(\\d*[.]\\d+ )[+-]?(\\d*[.]\\d+)(,[+-]?(\\d*[.]\\d+ )[+-]?(\\d*[.]\\d+))*$', {
		message: t(message || 'error.incorrectFormat'),
		excludeEmptyString: true
	});
});

yup.addMethod(yup.string, 'checkVatNumber', function (message) {
	return this.matches('^[a-zA-Z0-9- .]*$', {
		message: t(message || 'error.incorrectFormat'),
		excludeEmptyString: true
	});
});

yup.addMethod(yup.string, 'checkCRNumber', function (message) {
	return this.matches('^[a-zA-Z0-9-]*$', {
		message: t(message || 'error.incorrectFormat'),
		excludeEmptyString: true
	});
});

yup.addMethod(yup.string, 'iban', function (name) {
	return this.matches(IBAN_REGEX, {
		message: t('error.notValidIBANFormat'),
		name: name || 'BankIban',
		excludeEmptyString: true
	});
});
